import { useEffect, useRef, useState } from "react";
import { ShieldCheck, Mail, KeyRound } from "lucide-react";
import { http } from "../api/http";
import { Button } from "./ui/Button";
import "./AdminMfaDialog.css";
export function AdminMfaDialog() {
  const panel = useRef<HTMLElement>(null);
  const [request, setRequest] = useState<{
    resolve: () => void;
    reject: (e: Error) => void;
  } | null>(null);
  const [status, setStatus] = useState<{
    enrolled: boolean;
    canUsePassword: boolean;
    canUseEmail?: boolean;
  } | null>(null);
  const [secret, setSecret] = useState(""),
    [proof, setProof] = useState(""),
    [code, setCode] = useState(""),
    [error, setError] = useState(""),
    [info, setInfo] = useState("");
  const [recovery, setRecovery] = useState<string[]>([]),
    [busy, setBusy] = useState(false);
  const [useApp, setUseApp] = useState(false);
  useEffect(() => {
    if (!request) return;
    const previous = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = overflow;
      previous?.focus();
    };
  }, [request]);
  useEffect(() => {
    const listener = (e: Event) => {
      setRequest((e as CustomEvent).detail);
      setStatus(null);
      setError("");
      setInfo("");
      setSecret("");
      setProof("");
      setCode("");
      setRecovery([]);
      setUseApp(false);
      void http
        .get("/v1/auth/mfa")
        .then((r) => setStatus(r.data))
        .catch(() => setError("État MFA indisponible."));
    };
    window.addEventListener("permio:admin-mfa", listener);
    return () => window.removeEventListener("permio:admin-mfa", listener);
  }, []);
  const perform = async (action: () => Promise<void>) => {
    setBusy(true);
    setError("");
    try {
      await action();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? "Vérification non confirmée.");
    } finally {
      setBusy(false);
    }
  };
  const finish = () => {
    request?.resolve();
    setRequest(null);
    setSecret("");
    setProof("");
    setCode("");
    setRecovery([]);
  };
  if (!request) return null;
  return (
    <div className="modal admin-mfa">
      <div className="modal__scrim" aria-hidden="true" />
      <section
        ref={panel}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="admin-mfa-title"
        aria-describedby="admin-mfa-description"
        aria-busy={busy}
        className="modal__content admin-mfa__panel"
        onKeyDown={(e) => {
          if (e.key !== "Tab") return;
          const controls = Array.from(
            e.currentTarget.querySelectorAll<HTMLElement>(
              "button:not(:disabled), input:not(:disabled)",
            ),
          );
          const first = controls[0],
            last = controls[controls.length - 1];
          if (!first) {
            e.preventDefault();
            return;
          }
          if (
            e.shiftKey &&
            (document.activeElement === first ||
              document.activeElement === panel.current)
          ) {
            e.preventDefault();
            last.focus();
          } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }}
      >
        <header className="admin-mfa__header">
          <span className="admin-mfa__icon">
            <ShieldCheck size={26} aria-hidden="true" />
          </span>
          <div>
            <p className="admin-mfa__eyebrow">Sécurité du compte</p>
            <h2 id="admin-mfa-title">Vérification de votre connexion</h2>
          </div>
        </header>
        <p id="admin-mfa-description" className="admin-mfa__description">
          Votre compte individuel doit être vérifié avant de poursuivre.
        </p>
        {error && (
          <p
            className="admin-mfa__notice admin-mfa__notice--error"
            role="alert"
          >
            {error}
          </p>
        )}
        {info && (
          <p className="admin-mfa__notice" role="status">
            {info}
          </p>
        )}
        {!status && (
          <p className="admin-mfa__description" role="status">
            Chargement…
          </p>
        )}
        {status?.canUseEmail && !useApp && (
          <form
            className="admin-mfa__form"
            onSubmit={(e) => {
              e.preventDefault();
              void perform(async () => {
                await http.post("/v1/auth/mfa/email/verify", {
                  code: code.replace(/\s/g, ""),
                });
                finish();
              });
            }}
          >
            <p className="admin-mfa__hint">
              Recevez un code à l’adresse e-mail de votre compte pour continuer.
              Aucune application à installer.
            </p>
            <Button
              type="button"
              variant="secondary"
              disabled={busy}
              onClick={() =>
                void perform(async () => {
                  await http.post("/v1/auth/mfa/email/send-code");
                  setInfo(
                    "La demande d’envoi a été acceptée. Le code est valable 5 minutes.",
                  );
                })
              }
            >
              <Mail size={17} aria-hidden="true" /> Recevoir un code par e-mail
            </Button>
            <label className="admin-mfa__field">
              Code reçu par e-mail
              <input
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{8}"
                maxLength={8}
                value={code}
                onChange={(e) => setCode(e.target.value)}
                required
              />
            </label>
            <Button type="submit" disabled={busy} className="admin-mfa__submit">
              {busy ? "Vérification…" : "Vérifier"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                setUseApp(true);
                setInfo("");
                setError("");
                setCode("");
              }}
            >
              Utiliser une application d’authentification
            </Button>
          </form>
        )}
        {status?.canUseEmail && useApp && !secret && recovery.length === 0 && (
          <Button
            type="button"
            variant="ghost"
            disabled={busy}
            onClick={() => {
              setUseApp(false);
              setInfo("");
              setError("");
              setCode("");
              setProof("");
            }}
          >
            Utiliser un code par e-mail
          </Button>
        )}
        {status &&
          (!status.canUseEmail || useApp) &&
          !status.enrolled &&
          !secret &&
          recovery.length === 0 && (
            <form
              className="admin-mfa__form"
              onSubmit={(e) => {
                e.preventDefault();
                void perform(async () => {
                  const { data } = await http.post(
                    "/v1/auth/mfa/enroll",
                    status.canUsePassword
                      ? { password: proof }
                      : { emailCode: proof },
                  );
                  setSecret(data.secret);
                  setInfo("");
                  setProof("");
                });
              }}
            >
              <p className="admin-mfa__hint">
                Activez votre application d’authentification.
              </p>
              {status.canUsePassword && (
                <Button
                  variant="secondary"
                  className="admin-mfa__alternative"
                  type="button"
                  disabled={busy}
                  onClick={() => {
                    setProof("");
                    setStatus({ ...status, canUsePassword: false });
                  }}
                >
                  <Mail size={17} aria-hidden="true" /> Utiliser un code par
                  e-mail
                </Button>
              )}
              {!status.canUsePassword && (
                <Button
                  variant="secondary"
                  className="admin-mfa__alternative"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void perform(async () => {
                      await http.post("/v1/auth/mfa/send-code");
                      setInfo("La demande d’envoi du code a été acceptée.");
                    })
                  }
                >
                  <Mail size={17} aria-hidden="true" /> Recevoir un code par
                  e-mail
                </Button>
              )}
              <label className="admin-mfa__field">
                {status.canUsePassword
                  ? "Mot de passe actuel"
                  : "Code reçu par e-mail"}
                <input
                  type={status.canUsePassword ? "password" : "text"}
                  autoComplete={
                    status.canUsePassword ? "current-password" : "one-time-code"
                  }
                  value={proof}
                  onChange={(e) => setProof(e.target.value)}
                  required
                />
              </label>
              <Button
                type="submit"
                disabled={busy}
                className="admin-mfa__submit"
              >
                {busy ? "Préparation…" : "Préparer l’activation"}
              </Button>
            </form>
          )}
        {secret && (
          <div className="admin-mfa__setup">
            <p>
              Ajoutez un compte « Permio » dans votre application
              d’authentification avec cette clé :
            </p>
            <code className="admin-mfa__secret">{secret}</code>
            <p className="admin-mfa__hint">
              Conservez cette clé uniquement dans votre application
              d’authentification.
            </p>
          </div>
        )}
        {status &&
          (!status.canUseEmail || useApp) &&
          (status.enrolled || secret) &&
          recovery.length === 0 && (
            <form
              className="admin-mfa__form"
              onSubmit={(e) => {
                e.preventDefault();
                void perform(async () => {
                  const { data } = await http.post("/v1/auth/mfa/verify", {
                    code: code.replace(/\s/g, ""),
                  });
                  setCode("");
                  setSecret("");
                  if (data.recoveryCodes?.length)
                    setRecovery(data.recoveryCodes);
                  else finish();
                });
              }}
            >
              <label className="admin-mfa__field">
                Code d’authentification ou code de secours
                <input
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                />
              </label>
              <Button
                type="submit"
                disabled={busy}
                className="admin-mfa__submit"
              >
                {busy ? "Vérification…" : "Vérifier"}
              </Button>
            </form>
          )}
        {recovery.length > 0 && (
          <div className="admin-mfa__form">
            <h3 className="admin-mfa__recovery-title">
              <KeyRound size={19} aria-hidden="true" /> Vos codes de secours
            </h3>
            <p>
              Ces codes de secours ne seront affichés qu’une fois. Conservez-les
              hors de Permio ; chacun est utilisable une seule fois.
            </p>
            <pre className="admin-mfa__codes">{recovery.join("\n")}</pre>
            <Button onClick={finish}>
              J’ai conservé mes codes — continuer
            </Button>
          </div>
        )}
        <footer className="admin-mfa__footer">
          <Button
            variant="ghost"
            disabled={busy}
            onClick={() => {
              request.reject(new Error("Vérification MFA annulée"));
              setRequest(null);
              setSecret("");
              setProof("");
              setCode("");
            }}
          >
            Annuler
          </Button>
        </footer>
      </section>
    </div>
  );
}
